import { observer } from "mobx-react-lite";
import * as React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { mk8sMobx } from "../../../../mst/kinds/mk8s/mk8s";
import { Mk8sDraftMobx } from "../../../../mst/stores/mk8s.draft";
import { NGButton } from "../../../../newcomponents/button/Button";
import { NGFormElement } from "../../../../newcomponents/ngformelement/ngformelement";
import { NGInputListMst } from "../../../../newcomponents/inputList/inputListMst";
import { NGInput } from "../../../../newcomponents/input/input";
import { NGSelect } from "../../../../newcomponents/select/ngselect";
import { getDryRunErrorText } from "../../getDryRunErrorText";
import { mk8sDryRun } from "../../../../mobxStores/dryRun/mk8s";
import { DetectedTags, useHasDetectedTags } from "../../../../components/detail/detectedTags";
import { Dropdown, Modal, notification } from "antd";
import { request, resourceLink } from "../../../../services/cpln";
import { Mk8sScriptModal } from "../../Mk8sScriptModal";
import { Theme } from "../../../../mobxStores/uiData/theme";
import { ChevronDown } from "react-feather";
import { Mk8sDraftProviderGenericMobx } from "../../../../mst/stores/mk8s.draft.provider.generic";
import { GenericNodePoolMobx } from "../../../../mst/stores/mk8s.draft.provider.common";

interface Props {
  mk8s: mk8sMobx;
  mk8sDraft: Mk8sDraftMobx;
  generic: Mk8sDraftProviderGenericMobx;
  nodePool: GenericNodePoolMobx;
  index: number;
}
const ProviderGenericNodePoolRaw: React.FC<Props> = ({ mk8s, mk8sDraft, generic: g, nodePool: np, index }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [joinScriptIndex, setJoinScriptIndex] = React.useState<number>(-1);
  const [joinScript, setJoinScript] = React.useState<string>("");

  const [cloudinitScriptIndex, setCloudinitScriptIndex] = React.useState<number>(-1);
  const [cloudinitScript, setCloudinitScript] = React.useState<string>("");

  const hasDetectedTags = useHasDetectedTags({
    value: np.labels.items,
    detectValue: (labels, key) => labels.some((l) => l.firstValue === key),
    detectionMap: {
      "cpln.io/nodeType": "if value is core: nodes can accept BYOK core components",
    },
  });

  const pre = `generic.nodePools[${index}].`;

  const labelsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.generic.nodePools[${index}].labels`,
    paths: np.labels.items.map((i) => `spec.provider.generic.nodePools[${index}].labels.${i.firstValue}`),
  });
  const taintsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.generic.nodePools[${index}].taints`,
    paths: [
      ...np.taints.items.map((_, idx) => `spec.provider.generic.nodePools[${index}].taints[${idx}]`),
      ...np.taints.items.map((_, idx) => `spec.provider.generic.nodePools[${index}].taints[${idx}].key`),
      ...np.taints.items.map((_, idx) => `spec.provider.generic.nodePools[${index}].taints[${idx}].value`),
      ...np.taints.items.map((_, idx) => `spec.provider.generic.nodePools[${index}].taints[${idx}].effect`),
    ],
  });

  async function handleJoinScript(index: number) {
    const notificationKey: string = "joinScript";
    notification.open({
      key: notificationKey,
      type: "info",
      message: "Join Script",
      description: "Fetching data from the server, please wait",
    });

    try {
      setJoinScriptIndex(index);
      const {
        data: { script },
      } = await request({
        method: "post",
        url: resourceLink("mk8s", mk8sDraft._name) + "/-join",
        body: {
          nodePoolName: g.nodePools.find((np) => np.index === index)!.name.value,
        },
      });
      setJoinScript(script);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({ message: "Failed to create join script", description: errorMessage });
    }
    notification.destroy(notificationKey);
  }

  async function handleCloudinitScript(index: number) {
    const notificationKey: string = "cloudinitScript";
    notification.open({
      key: notificationKey,
      type: "info",
      message: "Cloud Init Script",
      description: "Fetching data from the server, please wait",
    });

    try {
      setCloudinitScriptIndex(index);
      const {
        data: { script },
      } = await request({
        method: "post",
        url: resourceLink("mk8s", mk8sDraft._name) + "/-cloudinit",
        body: {
          nodePoolName: g.nodePools.find((np) => np.index === index)!.name.value,
        },
      });
      setCloudinitScript(script);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({ message: "Failed to create cloud init script", description: errorMessage });
    }
    notification.destroy(notificationKey);
  }

  return (
    <>
      <div>
        <div className="flex justify-between">
          <NGFormElement
            name={`${pre}name`}
            label={np.name.label}
            value={np.name.value}
            onChange={np.name.setValue}
            required
            error={!g.isNodePoolNameValid(np.index) ? "Node pool names must be unique" : ""}
          />
          <div>
            <Dropdown
              trigger={["click"]}
              menu={{
                onClick: async function ({ key }: any) {
                  switch (key) {
                    case "remove":
                      g.removeNodePoolAt(np.index);
                      navigate(`${pathname.split("/-node-pools")[0]}/-node-pools`);
                      break;
                    case "join":
                      if (mk8sDraft.isDirty) {
                        Modal.warning({
                          title: "Node Pool Join Script",
                          content: "Please save your changes before trying to create the join script.",
                        });
                        return;
                      }

                      handleJoinScript(np.index);
                      break;
                    case "cloudinit":
                      if (mk8sDraft.isDirty) {
                        Modal.warning({
                          title: "Node Pool Cloud Init Script",
                          content: "Please save your changes before trying to create the cloud init script.",
                        });
                        return;
                      }
                      handleCloudinitScript(np.index);
                      break;
                    default:
                      break;
                  }
                },
                items: [
                  { key: "join", label: "Generate Join Script" },
                  { key: "cloudinit", label: "Generate Cloud Init Script" },
                  { key: "remove", label: "Remove", danger: true },
                ],
              }}
            >
              <NGButton
                variant={"primary"}
                size="small"
                outlined={Theme.theme === "light"}
                renderIcon={(hover, props) => <ChevronDown {...props} />}
              >
                Node Pool Actions
              </NGButton>
            </Dropdown>
          </div>
        </div>
        <NGInputListMst
          data={np.labels}
          className={hasDetectedTags ? "mb-2" : "mb-8"}
          label="Labels"
          labelRender={() => {
            return (
              <>
                {np.labels.items.some((l) => l.firstValue === "cpln.io/nodeType" && l.secondValue === "core") ? null : (
                  <NGButton
                    variant="primary"
                    outlined
                    className="ml-2"
                    onClick={() => {
                      const id = np.labels.add();
                      const item = np.labels.items.find((i) => i.id === id);
                      if (item) {
                        item.setFirstValue("cpln.io/nodeType");
                        item.setSecondValue("core");
                      }
                    }}
                    size={`small`}
                  >
                    Use this nodepool as BYOK core
                  </NGButton>
                )}
              </>
            );
          }}
          firstLabel="Key"
          firstInput={(i) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && labelsDryRun.message.includes(`.${i.firstValue}`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          secondLabel="Value"
          secondInput={(i) => {
            let invalid = false;
            if (!i.secondValue) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.secondValue}
                onChange={(e) => i.setSecondValue(e.target.value)}
              />
            );
          }}
          invalid={labelsDryRun.severity === "error"}
          error={labelsDryRun.severity === "error" ? labelsDryRun.message : ""}
          warning={labelsDryRun.severity === "error" ? "" : labelsDryRun.message}
        />
        <DetectedTags
          className="mb-8"
          value={np.labels.items}
          detectValue={(labels, key) => labels.some((l) => l.firstValue === key)}
          detectionMap={{
            "cpln.io/nodeType": "if value is core: nodes can accept BYOK core components",
          }}
          pluralLabel="Labels"
        />
        <NGInputListMst
          data={np.taints}
          className="mb-8"
          label="Taints"
          firstLabel="Key"
          firstInput={(i, index) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && taintsDryRun.message.includes(`[${index}].key`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          secondLabel="Value"
          secondInput={(i) => {
            let invalid = false;
            if (!i.secondValue) {
              invalid = true;
            }
            if (i.secondValue && taintsDryRun.message.includes(`[${index}].value`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.secondValue}
                onChange={(e) => i.setSecondValue(e.target.value)}
              />
            );
          }}
          thirdLabel="Effect"
          thirdInput={(i) => {
            let invalid = false;
            if (i.thirdValue && taintsDryRun.message.includes(`[${index}].effect`)) {
              invalid = true;
            }

            return (
              <NGSelect
                invalid={invalid}
                value={i.thirdValue}
                onChange={(value) => i.setThirdValue(value)}
                options={[
                  { label: "NoSchedule", value: "NoSchedule" },
                  { label: "PreferNoSchedule", value: "PreferNoSchedule" },
                  { label: "NoExecute", value: "NoExecute" },
                ]}
              />
            );
          }}
          invalid={taintsDryRun.severity === "error"}
          error={taintsDryRun.severity === "error" ? taintsDryRun.message : ""}
          warning={taintsDryRun.severity === "error" ? "" : taintsDryRun.message}
        />
      </div>
      {!!joinScript ? (
        <Mk8sScriptModal
          clusterName={mk8sDraft._name}
          nodePoolName={g.nodePools.find((np) => np.index === joinScriptIndex)!.name.value}
          script={joinScript}
          poolType={"Node Pool"}
          scriptName={"Join Script"}
          extension={"sh"}
          onDone={() => {
            setJoinScriptIndex(-1);
            setJoinScript("");
          }}
          visible={!!joinScript}
        />
      ) : null}
      {!!cloudinitScript ? (
        <Mk8sScriptModal
          clusterName={mk8sDraft._name}
          nodePoolName={g.nodePools.find((np) => np.index === cloudinitScriptIndex)!.name.value}
          script={cloudinitScript}
          poolType={"Node Pool"}
          scriptName={"Cloud Init Script"}
          extension={"yaml"}
          onDone={() => {
            setCloudinitScriptIndex(-1);
            setCloudinitScript("");
          }}
          visible={!!cloudinitScript}
        />
      ) : null}
    </>
  );
};

export const ProviderGenericNodePool = observer(ProviderGenericNodePoolRaw);
